<template>
  <div>
    <!-- === MODALS === -->

    <v-row>
      <v-spacer />
      <v-col cols="4">
        <!-- Choix du statut -->
        <v-select
            v-model="readingStatus"
            :label="$t('status')"
            :items="[
              {text: $t('meter-device.reading.status.PENDING'), value: 'PENDING'},
              {text: $t('meter-device.reading.status.ACCEPTED'), value: 'ACCEPTED'},
              {text: $t('meter-device.reading.status.REJECTED'), value: 'REJECTED'},
          ]"
        ></v-select>
      </v-col>
    </v-row>

    <!-- === TABLE === -->

    <v-data-table
        :headers="headers"
        :items="readings"
        :items-per-page="10"
        :loading="loading"
        :item-class="tableRowClasses"
    >
      <template v-slot:item.pictureUrl="{ item, value }">
        <v-btn class="btn-show-picture" text color="primary" v-if="value && value !== ''" @click="showPicture(item)">{{ $t('show-picture') }}</v-btn>
      </template>

      <template v-slot:item.readingValue="{ value }">
        <v-icon left v-if="value === 0">mdi-alert</v-icon>
        {{ value / 1000 }} {{ $t('usages.kwh') }}
      </template>

      <template #item.customerName="{ item }">
        <div v-if="item.customer">{{ item.customer.name }}</div>
        <div v-else><v-icon color="error" left>mdi-alert</v-icon></div>
      </template>

      <template #item.readingDate="{ value }">
        {{ new Date(value).toLocaleDateString() }}
      </template>

      <template #item.lastReading="{ item }">
        <div v-if="getLastReadingValue(item.meterDevice).value">
          {{ (getLastReadingValue(item.meterDevice).value / 1000) + " " + $t('usages.kwh') }} - {{ getLastReadingValue(item.meterDevice).date|formatDate('DD/MM/YYYY') }}
        </div>
        <div v-else>
          {{ $t('meter-device.reading.no-initial-value') }}
        </div>
      </template>

      <template #item.consumedEnergy="{ item }">
        <div v-if="isOlderReading(item)">
          <div v-if="energyConsumed(item)">
            {{ (energyConsumed(item) / 1000) + ' ' + $t('usages.kwh') }}
          </div>
          <div v-else>
            <v-icon color="error" left>mdi-alert</v-icon>
            {{ item.missingMeterMaxValue ? $t("meter-device.missing-max-value") : '' }}
          </div>
        </div>
      </template>


      <template #item.actions="{ item }">
        <v-btn icon small @click="openReading(item)">
          <v-icon>mdi-eye</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import MeterDeviceRepository from "@repository/MeterDeviceRepository";

export default {
  data() {
    return {
      showPictureDialog: {
        displayed: false,
        picture: null,
        loading: false,
      },
      editDialog: {
        displayed: false,
        readingId: null,
        readingValue: null,
        readingDate: null,
        meterDevice: null,
      },
      duplicatesCount: 0,
      isSubmitting: false,
      readings: [],
      loading: false,
      readingStatus: "PENDING",
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('date'), value: 'readingDate' },
        { text: this.$tc('meter-device.meter-device', 2), value: 'meterDevice.name' },
        { text: this.$tc('customer.customer', 1), value: 'customerName' },
        { text: "", value: 'actions', width: '150'},
      ]
    },


    tableRowClasses() {
      const duplicates = this.findDuplicates();
      return (item) => {
        return item.duplicated ? 'duplicate' : '';
      };
    },
  },

  watch: {
    'showPictureDialog.displayed'(val) {
      if (!val) {
        this.showPictureDialog.picture = null
      }
    },

    readingStatus() {
      this.loadData()
    }
  },

  mounted() {
    this.loadData()
  },

  methods: {
    loadData() {
      this.loading = true;

      MeterDeviceRepository.searchMeterDeviceReadings(null, null, this.readingStatus)
          .then(readings => {
            this.readings = readings

            readings.forEach(reading => {
              if (!reading.meterDevice.maxMeterValue) {
                reading.missingMeterMaxValue = true;
              }
            });

            this.loading = false;

            this.duplicatesCount = this.findDuplicates().length
          }).catch(err => {
        this.loading = false;
        console.log(err)
        this.notifyError(err)
      })
    },


    getLastReadingValue(meterDevice) {
      let value = meterDevice.lastMeterValue;
      let date = meterDevice.lastMeterDate;

      if(!value || !date) {
        value = meterDevice.initialMeterValue ?? 0;
        date = meterDevice.createdAt;
      }

      return {date, value};
    },

    isDateValid(reading) {
      if(!reading.meterDevice.lastMeterDate) {
        return true
      }
      return new Date(reading.readingDate) > new Date(reading.meterDevice.lastMeterDate);
    },

    findDuplicates() {
      const duplicates = [];

      // Double boucle pour comparer chaque élément avec tous les autres
      for (let i = 0; i < this.readings.length; i++) {
        for (let j = i + 1; j < this.readings.length; j++) {
          const item1 = this.readings[i];
          const item2 = this.readings[j];

          // Comparaison des clés (id du dispositif de mesure + date de lecture)
          const key1 = item1.meterDevice.id + item1.readingDate;
          const key2 = item2.meterDevice.id + item2.readingDate;

          // Si les clés sont identiques, cela signifie qu'il y a un doublon
          if (key1 === key2) {
            // Marquer les deux éléments comme doublons
            item1.duplicated = true;
            item2.duplicated = true;

            // Ajouter les deux éléments à la liste des doublons (si ce n'est pas déjà fait)
            if (!duplicates.includes(item1)) {
              duplicates.push(item1);
            }
            if (!duplicates.includes(item2)) {
              duplicates.push(item2);
            }
          }
        }
      }

      return duplicates;
    },

    energyConsumed(item) {
      let lastMeterValue = item.meterDevice.lastMeterValue ?? 0;
      return item.readingValue - lastMeterValue;
    },

    showPicture(reading) {
      this.showPictureDialog.id = null
      this.showPictureDialog.displayed = true
      this.showPictureDialog.loading = true

      MeterDeviceRepository.getMeterDeviceReadingPicture(reading.id)
          .then(result => {
            this.showPictureDialog.id = reading.id
            this.showPictureDialog.picture = result
            this.showPictureDialog.loading = false
          }).catch(err => {
        this.showPictureDialog.loading = false
        console.log(err)
        this.notifyError(err)
      })
    },


    isOlderReading(reading) {
      if (!reading || !reading.meterDevice || !reading.readingDate) {
        return false;
      }

      const meterDeviceReadings = this.readings.filter(item => item.meterDevice && item.meterDevice.id === reading.meterDevice.id);

      if (meterDeviceReadings.length === 0) {
        return false; // Pas d'autres lectures pour ce meterDevice
      }

      const readingDate = new Date(reading.readingDate);

      // Vérifier si la lecture actuelle est la plus ancienne
      return meterDeviceReadings.every(item => {
        const otherReadingDate = new Date(item.readingDate);
        return readingDate <= otherReadingDate;
      });
    },

    openReading(item) {
      window.open(this.$router.resolve({name: 'meter-device.readings', params: {id: item.meterDevice.id}}).href, "_blank")
    }
  }
}
</script>

<style lang="scss">
.btn-show-picture {
  font-size: 1rem;
}

.duplicate {
  background: rgba(255, 165, 0, 0.5);
}
</style>